import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";
import { Table as MuiTable } from "@mui/material";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import SoftBox from "StyleFeatures/SoftBox";
import SoftAvatar from "StyleFeatures/SoftAvatar";
import SoftTypography from "StyleFeatures/SoftTypography";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import borders from "assets/theme/base/borders";

function Table({ columns = [], rows = [{}] }) {
  const { light } = colors;
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;

  const renderColumns = columns.map(({ title, align, width }, index) => {
    if (!title) return null;
    const pl = index === 0 ? 3 : 1;
    const pr = index === columns.length - 1 ? 3 : 1;

    return (
      <SoftBox
        key={title}
        component="th"
        width={width || "auto"}
        pt={1.5}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        color="secondary"
        opacity={0.7}
        borderBottom={`${borderWidth[1]} solid ${light.main}`}
      >
        {title.toUpperCase()}
      </SoftBox>
    );
  });

  const renderRows = rows.map((row, key) => {
    const rowKey = `row-${key}`;

    const tableRow = columns.map(({ name, align }) => {
      const value = row[name];

      // 🧩 Case 1: If value is a React element (icons, buttons, etc.)
      if (React.isValidElement(value)) {
        return (
          <td
            key={uuidv4()}
            style={{
              textAlign: align,
              borderBottom: `${borderWidth[1]} solid ${light.main}`,
              cursor: "pointer",
              padding: "8px",
              userSelect: "none",
            }}
          >
            {value}
          </td>
        );
      }

      // 🧩 Case 2: Array (Avatar + name)
      if (Array.isArray(value)) {
        return (
          <td
            key={uuidv4()}
            style={{
              borderBottom: `${borderWidth[1]} solid ${light.main}`,
              padding: "8px",
            }}
          >
            <SoftBox display="flex" alignItems="center" py={0.5} px={1}>
              <SoftBox mr={2}>
                <SoftAvatar src={value[0]} name={value[1]} variant="rounded" size="sm" />
              </SoftBox>
              <SoftTypography variant="button" fontWeight="medium">
                {value[1]}
              </SoftTypography>
            </SoftBox>
          </td>
        );
      }

      // 🧩 Case 3: Fallback text
      return (
        <td
          key={uuidv4()}
          style={{
            textAlign: align,
            borderBottom: `${borderWidth[1]} solid ${light.main}`,
            padding: "8px",
          }}
        >
          <SoftTypography
            variant="button"
            fontWeight="regular"
            color="secondary"
            sx={{ display: "inline-block", width: "max-content" }}
          >
            {value ?? ""}
          </SoftTypography>
        </td>
      );
    });

    return <TableRow key={rowKey}>{tableRow}</TableRow>;
  });

  return useMemo(
    () => (
      <TableContainer>
        <MuiTable>
          <thead>
            <TableRow>{renderColumns}</TableRow>
          </thead>
          <TableBody>{renderRows}</TableBody>
        </MuiTable>
      </TableContainer>
    ),
    [columns, rows]
  );
}

Table.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.object),
  rows: PropTypes.arrayOf(PropTypes.object),
};

export default Table;
